import { BookingControls } from "./booking-policy.schema";
import { Schema as S } from "effect";

/** Shared scheduling contract for InsuranceType.
 * @since 1.1.8
 * @example InsuranceType;
 */
export const InsuranceType = S.Literals(["GKV", "PKV", "SELFPAY"]);
/** Shared scheduling contract for InsuranceType.
 * @since 1.1.8
 * @example InsuranceType;
 */
export type InsuranceType = S.Schema.Type<typeof InsuranceType>;

/**
 * Default patient-facing copy shown when a kiosk-visible appointment type must
 * be booked outside the kiosk.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * const copy = DEFAULT_KIOSK_APPOINTMENT_TYPE_DISABLED_REASON
 * ```
 */
export const DEFAULT_KIOSK_APPOINTMENT_TYPE_DISABLED_REASON =
  "Bitte vereinbaren Sie diesen Termin direkt am Empfang oder telefonisch.";

/**
 * Kiosk publication mode for a DocCirrus schedule type.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * const mode: KioskAppointmentTypePolicyMode = "disabledVisible"
 * ```
 */
export const KioskAppointmentTypePolicyMode = S.Literals(
  ["enabled",
  "disabledVisible",
  "hidden"],
);
/** Shared scheduling contract for KioskAppointmentTypePolicyMode.
 * @since 1.1.8
 * @example KioskAppointmentTypePolicyMode;
 */
export type KioskAppointmentTypePolicyMode = S.Schema.Type<
  typeof KioskAppointmentTypePolicyMode
>;

/**
 * Persisted kiosk policy for one DocCirrus appointment type.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * const policy: KioskAppointmentTypePolicy = {
 *   appointmentTypeId: "type-1",
 *   mode: "disabledVisible",
 *   disabledReason: DEFAULT_KIOSK_APPOINTMENT_TYPE_DISABLED_REASON,
 * }
 * ```
 */
export const KioskAppointmentTypePolicy = S.Struct({
  appointmentTypeId: S.String,
  mode: KioskAppointmentTypePolicyMode,
  disabledReason: S.optional(S.String),
  bookingControls: S.optional(BookingControls),
  version: S.optional(S.Int.pipe(S.check(S.isGreaterThanOrEqualTo(0)))),
});
/** Shared scheduling contract for KioskAppointmentTypePolicy.
 * @since 1.1.8
 * @example KioskAppointmentTypePolicy;
 */
export type KioskAppointmentTypePolicy = S.Schema.Type<
  typeof KioskAppointmentTypePolicy
>;

/**
 * Kiosk policy metadata returned with appointment types visible in the kiosk.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * const policy: KioskVisibleAppointmentTypePolicy = { mode: "enabled" }
 * ```
 */
export const KioskVisibleAppointmentTypePolicy = S.Struct({
  mode: S.Literals(["enabled", "disabledVisible"]),
  disabledReason: S.optional(S.String),
});
/** Shared scheduling contract for KioskVisibleAppointmentTypePolicy.
 * @since 1.1.8
 * @example KioskVisibleAppointmentTypePolicy;
 */
export type KioskVisibleAppointmentTypePolicy = S.Schema.Type<
  typeof KioskVisibleAppointmentTypePolicy
>;

/** Shared scheduling contract for AppointmentType.
 * @since 1.1.8
 * @example AppointmentType;
 */
export const AppointmentType = S.Struct({
  id: S.String,
  label: S.String,
  duration: S.optional(S.Number),
  description: S.optional(S.String),
  eligibleInsurances: S.optional(S.Array(InsuranceType)),
  calendarId: S.optional(S.String),
  kioskPolicy: S.optional(KioskVisibleAppointmentTypePolicy),
});

/** Shared scheduling contract for AppointmentType.
 * @since 1.1.8
 * @example AppointmentType;
 */
export type AppointmentType = S.Schema.Type<typeof AppointmentType>;

/** Shared scheduling contract for AppointmentTypeList.
 * @since 1.1.8
 * @example AppointmentTypeList;
 */
export const AppointmentTypeList = S.Array(AppointmentType);
/** Shared scheduling contract for AppointmentTypeList.
 * @since 1.1.8
 * @example AppointmentTypeList;
 */
export type AppointmentTypeList = S.Schema.Type<typeof AppointmentTypeList>;

/**
 * Persisted appointment-type policy list.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * const policies: KioskAppointmentTypePolicyList = []
 * ```
 */
export const KioskAppointmentTypePolicyList = S.Array(
  KioskAppointmentTypePolicy,
);
/** Shared scheduling contract for KioskAppointmentTypePolicyList.
 * @since 1.1.8
 * @example KioskAppointmentTypePolicyList;
 */
export type KioskAppointmentTypePolicyList = S.Schema.Type<
  typeof KioskAppointmentTypePolicyList
>;

/**
 * Request body for saving kiosk appointment-type policies.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * const request: UpsertKioskAppointmentTypePoliciesRequest = { policies: [] }
 * ```
 */
export const UpsertKioskAppointmentTypePoliciesRequest = S.Struct({
  expectedVersions: S.Record(S.String, S.Int.pipe(S.check(S.isGreaterThanOrEqualTo(0)))),
  policies: KioskAppointmentTypePolicyList,
});
/** Shared scheduling contract for UpsertKioskAppointmentTypePoliciesRequest.
 * @since 1.1.8
 * @example UpsertKioskAppointmentTypePoliciesRequest;
 */
export type UpsertKioskAppointmentTypePoliciesRequest = S.Schema.Type<
  typeof UpsertKioskAppointmentTypePoliciesRequest
>;

/**
 * Admin item combining a live DocCirrus appointment type with its kiosk policy.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * const item: AppointmentTypePolicyAdminItem = {
 *   appointmentType: { id: "type-1", label: "Kontrolle" },
 *   policy: { appointmentTypeId: "type-1", mode: "enabled" },
 * }
 * ```
 */
export const AppointmentTypePolicyAdminItem = S.Struct({
  appointmentType: AppointmentType,
  policy: KioskAppointmentTypePolicy,
});
/** Shared scheduling contract for AppointmentTypePolicyAdminItem.
 * @since 1.1.8
 * @example AppointmentTypePolicyAdminItem;
 */
export type AppointmentTypePolicyAdminItem = S.Schema.Type<
  typeof AppointmentTypePolicyAdminItem
>;

/**
 * Response body for the admin appointment-type policy screen.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * const response: AppointmentTypePolicySettingsResponse = { appointmentTypes: [] }
 * ```
 */
export const AppointmentTypePolicySettingsResponse = S.Struct({
  appointmentTypes: S.Array(AppointmentTypePolicyAdminItem),
});
/** Shared scheduling contract for AppointmentTypePolicySettingsResponse.
 * @since 1.1.8
 * @example AppointmentTypePolicySettingsResponse;
 */
export type AppointmentTypePolicySettingsResponse = S.Schema.Type<
  typeof AppointmentTypePolicySettingsResponse
>;

/** Staff-only inspection of an unsaved policy against current vendor associations.
 * @since 1.1.8
 * @example BookingPolicyPreviewRequest;
 */
export const BookingPolicyPreviewRequest = S.Struct({
  policy: KioskAppointmentTypePolicy,
  calendarId: S.NonEmptyString,
  targetDate: S.String,
});
/** Canonical staff preview input.
 * @since 1.1.8
 * @example BookingPolicyPreviewRequest;
 */
export type BookingPolicyPreviewRequest = S.Schema.Type<typeof BookingPolicyPreviewRequest>;
/** Permission preview; an allowed policy does not assert that a free slot exists.
 * @since 1.1.8
 * @example BookingPolicyPreviewResponse;
 */
export const BookingPolicyPreviewResponse = S.Struct({
  evaluatedAt: S.String,
  decision: S.Union(
    [S.Struct({ allowed: S.Literal(true), centralCalendarIds: S.Array(S.String) }),
    S.Struct({ allowed: S.Literal(false), reason: S.Literals(["type-disabled", "calendar", "date-window", "not-released"]) })],
  ),
  predecessorOpeningDate: S.optional(S.String),
  releaseInstant: S.optional(S.String),
});
/** Canonical staff preview output.
 * @since 1.1.8
 * @example BookingPolicyPreviewResponse;
 */
export type BookingPolicyPreviewResponse = S.Schema.Type<typeof BookingPolicyPreviewResponse>;
